import * as React from "react";
import { Check, ChevronsUpDown, Search } from "lucide-react";

import { cn } from "@/lib/cn";
import { Button } from "@/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Input } from "@/components/ui/input";

/**
 * SearchableSelect (Combobox)
 *
 * Canonical reference: ianfiles/ui-design-system.md §2.4 + Appendix C
 *
 * A Popover + filterable list pattern for select fields whose option count
 * exceeds what a stock `<Select>` can comfortably render (accounts, products,
 * vendors, sales reps). Built on raw Radix Popover + an internal `<Input>` —
 * we intentionally avoid the heavier cmdk/Command primitive for now because
 * it isn't installed and the surface area we actually need is small.
 *
 * Limitations vs. the canonical app:
 *   - This implementation does not (yet) accept a `variant="flat"` prop. The
 *     trigger always renders bordered. The legacy app calls this out as a
 *     deliberate gap; we'll add `flat` support once Phase 5/6 reveals a
 *     drawer that needs it.
 *   - Async option loading (e.g. server-side product search) is not built
 *     in. Pass an empty `options` array + a custom `emptyMessage` and lift
 *     the filter state via `value`/`onSearchChange` to do it yourself.
 */
export interface SearchableSelectOption<T extends string = string> {
  value: T;
  label: React.ReactNode;
  /** Optional secondary text shown muted to the right of `label`. */
  hint?: React.ReactNode;
  /** Whether this option should be disabled in the list. */
  disabled?: boolean;
  /** Hidden text used for client-side fuzzy matching (e.g. account code). */
  searchTerms?: string;
}

export interface SearchableSelectProps<T extends string = string> {
  /** Currently selected value. Pass `undefined` for "nothing selected". */
  value?: T;
  /** Called with the new value (or `undefined` if cleared). */
  onValueChange: (value: T | undefined) => void;
  options: SearchableSelectOption<T>[];
  /** Placeholder shown when no value is selected. */
  placeholder?: string;
  /** Placeholder shown inside the search input. */
  searchPlaceholder?: string;
  /** Message rendered when the filter returns no results. */
  emptyMessage?: React.ReactNode;
  /** Whether the user is allowed to clear the selection. */
  allowClear?: boolean;
  /** Tailwind className applied to the trigger button. */
  className?: string;
  /** Stable test id appended to the trigger. */
  "data-testid"?: string;
  disabled?: boolean;
}

export function SearchableSelect<T extends string = string>({
  value,
  onValueChange,
  options,
  placeholder = "Select…",
  searchPlaceholder = "Search…",
  emptyMessage = "No results",
  allowClear = false,
  className,
  disabled,
  "data-testid": dataTestId,
}: SearchableSelectProps<T>) {
  const [open, setOpen] = React.useState(false);
  const [query, setQuery] = React.useState("");
  const inputRef = React.useRef<HTMLInputElement>(null);

  const selected = options.find((o) => o.value === value);

  const filtered = React.useMemo(() => {
    if (!query) return options;
    const q = query.toLowerCase();
    return options.filter((o) => {
      const label = typeof o.label === "string" ? o.label : o.value;
      const haystack =
        `${label} ${o.searchTerms ?? ""} ${o.hint ?? ""}`.toLowerCase();
      return haystack.includes(q);
    });
  }, [options, query]);

  // Focus the search input on open
  React.useEffect(() => {
    if (open) {
      const t = setTimeout(() => inputRef.current?.focus(), 30);
      return () => clearTimeout(t);
    } else {
      setQuery("");
    }
  }, [open]);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          role="combobox"
          aria-expanded={open}
          disabled={disabled}
          data-testid={dataTestId}
          className={cn(
            "w-full justify-between font-normal",
            !selected && "text-muted-foreground",
            className
          )}
        >
          <span className="truncate text-left">
            {selected?.label ?? placeholder}
          </span>
          <ChevronsUpDown className="h-4 w-4 opacity-50 shrink-0" />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        className="p-0 w-[var(--radix-popover-trigger-width)] max-h-72 overflow-hidden"
      >
        <div className="flex items-center gap-2 px-3 py-2 border-b">
          <Search className="h-4 w-4 text-muted-foreground shrink-0" />
          <Input
            ref={inputRef}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={searchPlaceholder}
            className="h-7 border-0 bg-transparent shadow-none focus-visible:ring-0 focus-visible:ring-offset-0 px-0 text-sm"
          />
        </div>

        <div className="max-h-60 overflow-y-auto py-1">
          {allowClear ? (
            <SearchableOptionRow
              isSelected={value === undefined}
              onClick={() => {
                onValueChange(undefined);
                setOpen(false);
              }}
            >
              <span className="text-muted-foreground italic">— None —</span>
            </SearchableOptionRow>
          ) : null}

          {filtered.length === 0 ? (
            <div className="px-3 py-6 text-sm text-center text-muted-foreground">
              {emptyMessage}
            </div>
          ) : (
            filtered.map((option) => (
              <SearchableOptionRow
                key={option.value}
                isSelected={option.value === value}
                disabled={option.disabled}
                onClick={() => {
                  if (option.disabled) return;
                  onValueChange(option.value);
                  setOpen(false);
                }}
              >
                <span className="truncate">{option.label}</span>
                {option.hint ? (
                  <span className="ml-auto text-xs text-muted-foreground truncate">
                    {option.hint}
                  </span>
                ) : null}
              </SearchableOptionRow>
            ))
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}

function SearchableOptionRow({
  isSelected,
  disabled,
  onClick,
  children,
}: {
  isSelected: boolean;
  disabled?: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      role="option"
      aria-selected={isSelected}
      disabled={disabled}
      onClick={onClick}
      className={cn(
        "relative flex w-full items-center gap-2 rounded-sm px-3 py-1.5 text-sm",
        "outline-none text-left",
        "hover-elevate active-elevate-2",
        disabled && "pointer-events-none opacity-50",
        isSelected && "font-medium"
      )}
    >
      <Check
        className={cn(
          "h-4 w-4 shrink-0",
          isSelected ? "opacity-100" : "opacity-0"
        )}
      />
      {children}
    </button>
  );
}
